// profile-upgrade-dialog.tsx
import { FC, useState } from 'react'
import { useAuth } from 'store/useAuth'
import { useLocalStorage } from 'hooks/use-localstorage'
import { X as XIcon } from 'icons/x'
import { toast } from 'react-hot-toast'
import api from 'utils/api'

import {
  Avatar,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material'
import { useFetchProfile } from 'hooks/use-fetch-profile'
import { CouponCodeInput } from 'components/coupon-code-input'
import { UPGRADE_COUPON_STORAGE_KEY } from 'utils/upgrade-coupon'
import { useRouter } from 'next/router'

interface Feature {
  name: string
  description: string
}

interface ProfileUpgradeDialogProps {
  open: boolean
  handleClose: () => void
  id?: string
  dontShowModalAgain?: boolean
  title?: string
  description?: string
  buttonText?: string
  plan?: {
    name: string
    addons: string[]
    features: Feature[]
  }
}

export const ProfileUpgradeDialog: FC<ProfileUpgradeDialogProps> = ({
  handleClose,
  open,
  id,
  dontShowModalAgain = true,
  title,
  description,
  buttonText,
  plan,
}) => {
  const [hostedPageUrl, setHostedPageUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const { user } = useAuth()
  const { fetch } = useFetchProfile()
  const [dontShowAgain, setDontShowAgain] = useLocalStorage<boolean>(
    `dontShowAgain-${id}`,
    false,
  )
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [appliedCoupon, setAppliedCoupon] = useState(null)
  const router = useRouter()

  console.log({ plan })

  const handleDontShowAgain = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDontShowAgain(event.target.checked)
    if (event.target.checked) {
      handleClose()
    }
  }

  const handleApplyCoupon = async couponCode => {
    setLoading(true)
    setError(null)
    setSuccess(null)
    setHostedPageUrl(null)

    try {
      // Validated server-side against Stripe. Accepts either a promotion code
      // or a coupon id; the server resolves both to a coupon id.
      const response = await api.post('/agents/validate-coupon', {
        coupon: couponCode,
      })

      if (response.data?.valid) {
        const { amountOff, percentOff } = response.data
        const discountLabel = amountOff
          ? `$${(amountOff / 100).toFixed(2)} off`
          : percentOff
          ? `${percentOff}% off`
          : 'discount applied'

        setSuccess(`Coupon "${couponCode}" applied — ${discountLabel}.`)
        setAppliedCoupon(couponCode)
      } else {
        setError(response.data?.message ?? 'Invalid coupon code.')
      }
    } catch (err) {
      setError('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleCancelCoupon = () => {
    setError(null)
    setSuccess(null)
    setAppliedCoupon(null)
    setHostedPageUrl(null)
  }

  const handleUpgradeStripe = async () => {
    setIsLoading(true)
    const activeSubscription = user.subscriptions?.find(
      subscription => subscription.status === 'active',
    )
    if (!activeSubscription) {
      toast.error('No active subscription found')
      return
    }
    try {
      const response = await api.post('/agents/change-subscription', {
        memberId: user.id,
        subscriptionId: activeSubscription.externalId,
        subscriptionItemId:
          activeSubscription.subscriptionLineItems?.[0]?.externalId,
        newPriceId: 'price_1RgjTCEOlqojXwA85iVH9lGx',
        changeType: 'upgrade',
      })

      setTimeout(() => {
        fetch()
      }, 2000)
      // setHostedPageUrl(response.data.hosted_page)
      // window.open(response.data.hosted_page, '_blank')
    } catch (error) {
      toast.error('Error upgrading to premium')
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleUpgrade = async () => {
    if (hostedPageUrl) {
      window.open(hostedPageUrl, '_blank')
      return
    }

    setIsLoading(true)
    try {
      const response = await api.post('/agents/zoho-hosted-page', {
        member_id: user.id,
        plan: plan?.name,
        addons: plan?.addons,
        coupon: appliedCoupon,
      })
      setHostedPageUrl(response.data.hosted_page)
      window.open(response.data.hosted_page, '_blank')
    } catch (error) {
      toast.error('Error upgrading to premium')
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog onClose={handleClose} open={open} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">
          {title}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme => theme.palette.grey[500],
            }}
          >
            <XIcon fontSize="small" />
          </IconButton>
        </Typography>
        <Typography
          sx={{
            backgroundColor: 'background.default',
            borderRadius: 1,
            flexGrow: 1,
            mt: 1,
            p: 2,
          }}
          variant="body2"
        >
          {description}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <List>
              {plan?.features?.map((feature, index) => (
                <ListItem key={index}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <CheckCircleIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={feature.name}
                    secondary={feature.description}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
        <CouponCodeInput
          onApply={handleApplyCoupon}
          onCancel={handleCancelCoupon}
          loading={loading}
          error={error}
          success={success}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        {dontShowModalAgain && (
          <FormControlLabel
            control={
              <Checkbox
                checked={dontShowAgain}
                onChange={handleDontShowAgain}
              />
            }
            label="Don't show again"
            sx={{ ml: 1 }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <LoadingButton
          loading={isLoading}
          disabled={isLoading}
          onClick={() => {
            // Stripe is the billing system for new and existing Stripe members;
            // only legacy Zoho subscribers keep going to the hosted page.
            //
            // This used to test `subscriptions[0].meta.source == 'pro-onboarder'`,
            // which asks "has this member already bought through pro-onboard?".
            // A member with no subscription at all — i.e. everyone this upgrade
            // dialog is aimed at — has `subscriptions[0]` undefined, so the test
            // was false and they were sent to Zoho and charged 1MONTHFREE, then
            // full price. Keying on the presence of a Zoho subscription instead
            // sends "no subscription" to Stripe, where the brand discount and
            // coupon box apply.
            //
            // Verified against all 10,000 production subscription rows:
            // 6,215 carry meta.stripeCustomerId (Stripe), 3,737 have meta
            // without it (Zoho), and 48 have meta NULL. No row has
            // meta.source = 'pro-onboarder' without a stripeCustomerId.
            //
            // The 48 meta-null rows belong to 26 members whose subscriptions are
            // all meta-null; `subscription?.meta &&` makes those falsy, so they
            // route to Stripe. That is intended: a row with no meta carries no
            // Zoho identity, and Stripe is the system of record going forward.
            const hasZohoSubscription = user.subscriptions?.some(
              subscription =>
                subscription?.meta && !subscription.meta.stripeCustomerId,
            )

            if (!hasZohoSubscription) {
              // handleUpgradeStripe()

              // Hand the validated coupon to the account tab, which owns the
              // Stripe checkout call. sessionStorage rather than a query param:
              // pages/account.tsx rewrites the URL to `?tab=<current>` on mount,
              // which would drop any extra param before the tab renders.
              // Scoped to the tab and cleared once consumed.
              if (appliedCoupon) {
                window.sessionStorage.setItem(
                  UPGRADE_COUPON_STORAGE_KEY,
                  appliedCoupon,
                )
              }

              // Close before navigating: router.push swaps the page underneath
              // the dialog, which otherwise stays open over the account tab.
              handleClose()
              router.push('/account?tab=subscription')
            } else {
              handleUpgrade()
            }
          }}
          variant="contained"
          color="primary"
        >
          {buttonText}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
